import { useEffect, useState } from 'react';
import {
  api, darf, kalenderwoche, type Anmelder,
} from '../api.ts';
import { Kopf } from '../teile/Geruest.tsx';
import { Fehlerkasten, ausFehler, type Fehlerlage } from '../teile/Formular.tsx';
import { useFrisch } from '../teile/Frisch.tsx';
import {
  heuteTag, istWochenende, jahrVon, plusTage, wochentagNr,
} from '../teile/rastertage.ts';

/**
 * Der Verfügbarkeitskalender, so wie ihn die Firma selbst führt.
 *
 * ORIGINALTEXT MARVIN (26.08.2026)
 *   „Außerdem müssen sie in der App den Verfügbarkeitskalender führen."
 *
 * WARUM EIN MONATSKALENDER UND KEINE LISTE. Der Bestand hatte erst eine Liste
 * über 60 Tage am Stück, und der Kommentar dort sagt, woran sie gescheitert
 * ist: „wer im Oktober Urlaub eintragen wollte, kam nicht hin, und ohne
 * Kalenderwochen liess sich mit dem Büro nicht über ,KW 34' reden."
 *
 * DREI ZUSTÄNDE, NICHT ZWEI: verfügbar, Urlaub, keine Angabe. Ein Tag ohne
 * Eintrag gilt als FREI - das ist die Voreinstellung des Bestands, und wer sie
 * umdreht, sperrt über Nacht jeden Termin. „Keine Angabe" ist deshalb etwas
 * anderes als „nicht verfügbar" und muss sich auch wieder herstellen lassen.
 *
 * ZWEI MODI. Einzelne Tage antippen, oder einen Zeitraum aufspannen und einmal
 * entscheiden. Der Grund steht früher: „vierzehn Tage Urlaub hiessen
 * vierzehn Antipper, und wer sich verzaehlt, merkt es nicht."
 */

type Stand = 'V' | 'U' | '';

const WOCHENTAGE = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
const MONATE = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli',
                'August', 'September', 'Oktober', 'November', 'Dezember'];

/** Ein Tag aus Jahr, Monat und Tag. KEINE Uhr im Spiel: die drei Zahlen
 *  kommen aus einem Kalenderraster und sind schon der Tag, den sie meinen.
 *  Wer daraus ein `Date` baute, um es wieder zu zerlegen, holte sich die
 *  Zeitzone des Betrachters ins Spiel, die hier gar nichts zu suchen hat. */
function iso(j: number, m: number, t: number): string {
  return `${j}-${String(m + 1).padStart(2, '0')}-${String(t).padStart(2, '0')}`;
}

/* DIE KALENDERWOCHE STEHT IN `api.ts` UND NUR DORT. Hier stand die dritte
   Fassung dieser Rechnung, und sie ging über UTC statt über Ortszeit - in
   einer Zeitzone östlich von Greenwich fällt der Montag damit an einem Teil
   des Jahres in die Vorwoche.

   UND SEIT DEM 10.09.2026 RECHNET DIESER KALENDER OHNE JEDE UHR.
   ==========================================================================
   ORIGINALTEXT MARVIN (mit 2 Bildschirmfotos)
     „bild 1: von kamil, deutsche zeitzone, bild 2: mein pc, zypern zeitzone.
      aber das problem sind die wochentage, datum."

   `new Date(jahr, monat, 1)` ist der Monatsanfang auf der Uhr des
   BETRACHTERS, und `heuteIso` kam aus derselben Uhr - waehrend jeder Tag,
   der zum Server ging, in BERLIN gelesen wurde. **Ein Wert, den 2 Uhren
   lesen, ist 2 Werte.** */

/* .592: DERSELBE KALENDER FÜR NE4, JE TEAM. Marvin, 02.11.2026: „einen flexiblen
   Verfügbarkeitskalender wie in NE3 ... Du sollst das Rad nicht neu erfinden." Mit `ne4`
   wählt oben das Team, gelesen und gespeichert wird je Team. Alles andere ist dieser
   Kalender, mit Samstag und Sonntag wie in NE3 (.593: „alles andere steuern wir jetzt wie
   bei NE3 über die Verfügbarkeit"). */
export default function Verfuegbarkeit({
  ich, ne4 = false,
}: { ich: Anmelder; ne4?: boolean }) {
  const [teams, setTeams] = useState<any[]>([]);
  const [team, setTeam] = useState<string>('');
  /* „HEUTE" IST DIE ZEIT DES BETRIEBS und nicht die des Betrachters: wer in
     Zypern um 00:30 Ortszeit Urlaub eintraegt, arbeitet in einem Betrieb, in
     dem noch der Vortag laeuft. */
  const heuteIso = heuteTag();

  const [jahr, setJahr] = useState(() => jahrVon(heuteIso));
  const [monat, setMonat] = useState(() => Number(heuteIso.slice(5, 7)) - 1);
  const [bestand, setBestand] = useState<Record<string, Stand>>({});
  const [aenderungen, setAenderungen] = useState<Record<string, Stand>>({});
  const [modus, setModus] = useState<'einzeln' | 'zeitraum'>('einzeln');
  const [von, setVon] = useState<string | null>(null);
  const [bis, setBis] = useState<string | null>(null);
  const [nurWerktage, setNurWerktage] = useState(true);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [gespeichert, setGespeichert] = useState(false);

  const darfPflegen = darf(ich, ne4 ? 'ne4.verfuegbarkeit.pflegen' : 'verfuegbarkeit.pflegen');

  async function laden() {
    try {
      // Ein Monat voraus und einer zurueck: wer blaettert, soll nicht bei
      // jedem Klick warten, und mehr als das braucht der Kalender nicht.
      const von = iso(jahr, monat, 1);
      const bis = iso(monat === 11 ? jahr + 1 : jahr, (monat + 1) % 12, 1);
      const d: any = ne4 ? await api.ne4Verfuegbarkeit(von, bis, team || undefined) : await api.verfuegbarkeit(von, bis);
      if (ne4) { setTeams(d.teams ?? []); if (!team && d.team) setTeam(d.team); }
      const b: Record<string, Stand> = {};
      for (const z of d.zeilen ?? []) {
        b[String(z.datum).slice(0, 10)] = z.zustand === 'urlaub' ? 'U' : 'V';
      }
      setBestand(b);
      setFehler(null);
    } catch (f) { setFehler(ausFehler(f)); }
  }
  useEffect(() => { laden(); }, [jahr, monat, team]);
  // Ein anderes Team: was noch nicht gespeichert war, gehörte zum alten.
  useEffect(() => { setAenderungen({}); setGespeichert(false); }, [team]);
  useFrisch(laden);

  /** Erst die Änderung, dann der Bestand. `in` statt Wahrheitswert: „" ist
   *  ein echter Wert und bedeutet „keine Angabe". */
  const standVon = (t: string): Stand =>
    t in aenderungen ? aenderungen[t] : (bestand[t] ?? '');

  /** Landet man wieder auf dem gespeicherten Stand, ist es KEINE Änderung
   *  mehr - der Zähler geht zurück und der Knopf wird wieder grau. */
  function setze(tage: string[], neu: Stand) {
    const a = { ...aenderungen };
    for (const t of tage) {
      if ((bestand[t] ?? '') === neu) delete a[t];
      else a[t] = neu;
    }
    setAenderungen(a);
    setGespeichert(false);
  }

  function antippen(t: string) {
    if (!darfPflegen || t < heuteIso) return;
    if (modus === 'zeitraum') {
      if (!von || (von && bis)) { setVon(t); setBis(null); }
      else if (t < von) { setBis(von); setVon(t); }
      else setBis(t);
      return;
    }
    const jetzt = standVon(t);
    setze([t], jetzt === '' ? 'V' : jetzt === 'V' ? 'U' : '');
  }

  /** Die Tage des gewählten Zeitraums. Gefiltert wird ZWEIMAL: Wochenenden,
   *  wenn nur Werktage gewollt sind, und alles vor heute. */
  function zeitraumTage(): string[] {
    if (!von) return [];
    const ende = bis ?? von;
    const raus: string[] = [];
    /* GEZAEHLT WIRD IN KALENDERTAGEN UND NICHT IN MILLISEKUNDEN. Eine Woche
       ueber die Zeitumstellung hat 169 Stunden; wer `+ 86400000` rechnet,
       landet an dem Tag zweimal auf demselben Datum. */
    for (let t = von; t <= ende; t = plusTage(t, 1)) {
      if ((!nurWerktage || !istWochenende(t)) && t >= heuteIso) raus.push(t);
    }
    return raus;
  }

  async function speichern() {
    const gesendet = { ...aenderungen };
    setLaeuft(true); setFehler(null);
    try {
      const liste = Object.entries(gesendet).map(([datum, stand]) => ({
          datum,
          stand: stand === 'U' ? 'urlaub' as const
               : stand === 'V' ? 'frei' as const : 'weg' as const,
        }));
      if (ne4) await api.ne4VerfuegbarkeitSpeichern(team, liste);
      else await api.verfuegbarkeitMehrere(liste);
      // NUR WEGNEHMEN, WAS UNVERÄNDERT DURCHGING. Wer während der Laufzeit
      // noch einen Tag antippt, sah ihn früher sonst grün und den Knopf
      // grau - beim Server war er nie angekommen.
      setAenderungen((jetzt) => {
        const rest = { ...jetzt };
        for (const [t, w] of Object.entries(gesendet)) {
          if (rest[t] === w) delete rest[t];
        }
        return rest;
      });
      await laden();
      setGespeichert(true);
    } catch (f) { setFehler(ausFehler(f)); }
    finally { setLaeuft(false); }
  }

  // --- Das Gitter ----------------------------------------------------------
  const erster = iso(jahr, monat, 1);
  const versatz = wochentagNr(erster);            // Woche beginnt Montag
  /* WIE VIELE TAGE DER MONAT HAT: der Tag vor dem 1. des Folgemonats.
     `new Date(jahr, monat + 1, 0)` haette dasselbe gesagt und wieder eine Uhr
     gebraucht. */
  const tageImMonat = Number(
    plusTage(monat === 11 ? iso(jahr + 1, 0, 1) : iso(jahr, monat + 1, 1), -1)
      .slice(8, 10));
  const zellen: (string | null)[] = [];
  for (let i = 0; i < versatz; i++) zellen.push(null);
  for (let t = 1; t <= tageImMonat; t++) zellen.push(iso(jahr, monat, t));
  while (zellen.length % 7 !== 0) zellen.push(null);
  const wochen: (string | null)[][] = [];
  for (let i = 0; i < zellen.length; i += 7) wochen.push(zellen.slice(i, i + 7));

  const offen = Object.keys(aenderungen).length;
  const gewaehlt = new Set(modus === 'zeitraum' ? zeitraumTage() : []);

  function blaettern(n: number) {
    const m = monat + n;
    setJahr(jahr + Math.floor(m / 12));
    setMonat(((m % 12) + 12) % 12);
  }

  return (
    <>
      <Kopf titel={ne4 ? 'NE4, Verfügbarkeit' : 'Verfügbarkeit'}
            weg={ne4 ? 'Freie Tage und Urlaub je Team; Samstag und Sonntag als verfügbar melden, wenn gearbeitet wird'
                 : ich.firma ? `Freie Tage und Urlaub von ${ich.firma}`
                           : 'Freie Tage und Urlaub je Firma'}
            rechts={ne4 && teams.length > 0 ? (
              <select value={team} onChange={(e) => setTeam(e.target.value)} style={{ minWidth: 220 }}>
                {teams.map((t: any) => <option key={t.id} value={t.id}>{t.name}{teams.some((x: any) => x.firma !== t.firma) ? ` (${t.firma})` : ''}</option>)}
              </select>) : undefined} />
      <div className="inhalt">
        <Fehlerkasten lage={fehler} />

        <div className="karte kalender">
          <div className="leib">
            <div className="reihe kalenderkopf">
              <button className="knopf" onClick={() => blaettern(-1)}
                      aria-label="Monat zurück">‹</button>
              <b className="wachsen mitte">{MONATE[monat]} {jahr}</b>
              <button className="knopf" onClick={() => blaettern(1)}
                      aria-label="Monat vor">›</button>
            </div>

            <div className="reihe legende klein leise">
              <span><i className="punkt v" /> verfügbar</span>
              <span><i className="punkt u" /> Urlaub</span>
              <span><i className="punkt o" /> keine Angabe</span>
            </div>

            {darfPflegen && (
              <div className="reihe" style={{ marginBottom: 10 }}>
                {([['einzeln', 'Einzelne Tage'],
                   ['zeitraum', 'Zeitraum']] as const).map(([m, t]) => (
                  <button key={m}
                          className={'knopf filter' + (modus === m ? ' gewaehlt' : '')}
                          onClick={() => { setModus(m); setVon(null); setBis(null); }}>
                    {t}
                  </button>
                ))}
              </div>
            )}

            <table className="kalendergitter">
              <thead>
                <tr>
                  <th className="kw">KW</th>
                  {WOCHENTAGE.map((w) => <th key={w}>{w}</th>)}
                </tr>
              </thead>
              <tbody>
                {wochen.map((w, i) => {
                  const erste = w.find(Boolean);
                  return (
                    <tr key={i}>
                      <td className="kw">
                        {erste ? kalenderwoche(new Date(erste + 'T12:00:00')) : ''}
                      </td>
                      {w.map((t, j) => {
                        if (!t) return <td key={j} className="verf-tag fremd" />;
                        const s = standVon(t);
                        const vergangen = t < heuteIso;
                        const klassen = ['verf-tag',
                          s === 'V' ? 'v' : s === 'U' ? 'u' : 'o',
                          vergangen ? 'vergangen' : '',
                          t === heuteIso ? 'heute' : '',
                          t in aenderungen ? 'geaendert' : '',
                          gewaehlt.has(t) ? 'gewaehlt' : ''].filter(Boolean);
                        return (
                          <td key={j} className={klassen.join(' ')}
                              /* VERGANGENE TAGE BEKOMMEN GAR KEIN onClick.
                                 Nicht nur ausgegraut, sondern tot: ein
                                 ausgegrauter Knopf, der doch reagiert, ist
                                 schlimmer als einer, der es nicht tut. */
                              onClick={vergangen ? undefined : () => antippen(t)}>
                            {Number(t.slice(8))}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {darfPflegen && modus === 'zeitraum' && (
              <div className="hinweis" style={{ marginTop: 12 }}>
                {!von ? 'Ersten Tag antippen, dann den letzten.'
                  : !bis ? `Ab ${von.slice(8)}.${von.slice(5, 7)}. Jetzt den `
                           + 'letzten Tag antippen, oder gleich unten wählen '
                           + 'für diesen einen Tag.'
                  : `${von.slice(8)}.${von.slice(5, 7)}. bis `
                    + `${bis.slice(8)}.${bis.slice(5, 7)}., `
                    + `${zeitraumTage().length} Tage`}
                {(
                <label className="wahlzeile klein" style={{ marginTop: 8, gap: 6 }}>
                  <input type="checkbox" checked={nurWerktage}
                         onChange={(e) => setNurWerktage(e.target.checked)} />
                  nur Montag bis Freitag
                </label>)}
                <div className="reihe" style={{ marginTop: 8 }}>
                  {([['V', 'verfügbar'], ['U', 'Urlaub'],
                     ['', 'keine Angabe']] as const).map(([w, t]) => (
                    <button key={t} className="knopf" disabled={!von}
                            onClick={() => { setze(zeitraumTage(), w);
                                             setVon(null); setBis(null); }}>
                      {t}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {darfPflegen && (
              <>
                <p className="klein leise" style={{ marginTop: 12 }}>
                  {modus === 'einzeln'
                    ? 'Antippen wechselt: keine Angabe → verfügbar → Urlaub → '
                      + 'keine Angabe.'
                    : '2 Tipper spannen den Zeitraum auf, dann einmal '
                      + 'entscheiden. Ein dritter Tipp beginnt von vorn.'}
                  {' '}Vergangene Tage lassen sich nicht mehr ändern. Ein Tag
                  ohne Eintrag gilt als frei.
                </p>
                <div className="reihe wegknoepfe">
                  <button className="knopf haupt" disabled={laeuft || !offen}
                          onClick={speichern}>
                    {laeuft ? 'Einen Moment'
                      : offen ? `Speichern (${offen} ${offen === 1 ? 'Änderung'
                                                                  : 'Änderungen'})`
                      : gespeichert ? 'Gespeichert' : 'Speichern'}
                  </button>
                </div>
              </>
            )}
            {!darfPflegen && (
              <p className="klein leise" style={{ marginTop: 12 }}>
                Nur zum Ansehen. Zum Pflegen fehlt das Recht
                „{ne4 ? 'ne4.verfuegbarkeit.pflegen' : 'verfuegbarkeit.pflegen'}".
              </p>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
